import React, { useState, useCallback, useRef, useEffect, useContext, useMemo, Suspense, lazy } from "react";
import { TouchableOpacity, ScrollView, View, Image, Pressable, Platform, Dimensions, Linking, useWindowDimensions } from "react-native";
import { useSelector } from 'react-redux';
import { selectUserBilling } from '@reduxLocal/selectors/userSelectors';
import { Title, Card, Text } from '@constants/Themed';
import { DefaultRootState } from '@reduxLocal/persistState';
import DefaultScreen from '@components/Global/DefaultScreenView';
import PlanComponent from './Components/PlanComponent.web';
// Import Stripe UpgradeComponent - web version
import StripeUpgradeComponent from './Components/StripeUpgradeComponent';
// Import native Stripe component - uses native styling
import StripeUpgradeComponentNative from './Components/StripeUpgradeComponent.native';
import { IAPContext } from '@context/IAPContext';

// Lazy load native component only on native platforms - this prevents it from loading on web
// IMPORTANT: This component is ONLY loaded when needed, keeping IAP completely isolated
const UpgradeComponentNative = (Platform.OS === 'ios' || Platform.OS === 'android')
  ? lazy(() => {
    return import('./Components/UpgradeComponent.native');
  })
  : null;
import apis from '@api/api';
import { Separator, XStack, YStack, H2, H4, H6, Tabs, Spinner } from 'tamagui';
import ErrorBoundary from "@utils/ErrorBoundary";
import { MaterialIcons, Feather } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import styles from "@stylesheet";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import handleCancelPlan, { PlanType } from './utils/handleCancelPlan';
import { AlertDialogContext } from '@components/Global/AlertDialog/AlertDialogProvider';
import { HIDE_PAYMENT_REQUIRED } from '@reduxLocal/actions/types';
import { useDispatch } from 'react-redux';
import InformationalText from '@components/Global/InformationalText';
import AICreditsComponent from './Components/AICreditsComponent';


const UpgradeScreen = ({ navigation }) => {
  const company = useSelector((state: DefaultRootState) => state.promiseData.data.companyData || null);
  const userBilling = useSelector(selectUserBilling);
  // Get companyNumber from company data - backend uses this from session to determine billing context
  const companyNumber = company?._id || company?.id || null;
  const [userPlan, setUserPlan] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState<'monthly' | 'annual'>('monthly');
  const [planGroups, setPlanGroups] = useState([]);

  // Determine if we should use native component
  const isNative = Platform.OS === 'ios' || Platform.OS === 'android';

  // Default tab value for native platforms (IAP or Stripe)
  // Use useRef to persist across re-renders and prevent reset from IAP calls
  const defaultTab: 'iap' | 'stripe' = isNative ? 'iap' : 'stripe';
  const activeTabRef = useRef<'iap' | 'stripe'>(defaultTab);
  const [activeTab, setActiveTab] = useState<'iap' | 'stripe'>(defaultTab);

  // CRITICAL: Initialize ref on mount and keep it in sync
  // This prevents IAP component re-renders from resetting the tab
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Prevent tab from being reset by external re-renders
  // Only allow changes through explicit button clicks
  const setActiveTabStable = useCallback((newTab: 'iap' | 'stripe') => {
    activeTabRef.current = newTab;
    setActiveTab(newTab);
  }, [activeTab]);

  // Log tab changes for debugging
  useEffect(() => {
    // When switching tabs, ensure proper cleanup
    if (isNative) {
      if (activeTab === 'stripe') {
      } else if (activeTab === 'iap') {
      }
    }
  }, [activeTab, isNative]);
  const upgradeRef = useRef(null);
  const billingManageRef = useRef(null);
  const scrollRef = useRef<any>(null); // For native component
  const { width: screenWidth } = useWindowDimensions();
  const width = screenWidth - 50;
  const customerId = userBilling?.billingId;
  const theme = themeSelector();
  const dispatch = useDispatch();
  const alertContext = useContext(AlertDialogContext);

  // IAP context for restore purchases - use useContext directly to avoid hook errors
  const iapContextValue = useContext(IAPContext);

  const handleCustomerPortal = async () => {
    await apis.getCustomerPortal({ accountId: customerId }).then((res) => {
      if (res.url) {
        if (Platform.OS === 'web') {
          window.open(res.url, '_blank');
        } else {
          Linking.openURL(res.url);
        }
      }
    })
  }

  const _planCallback = useCallback((plan) => {
    setUserPlan(plan);
  }, [userPlan]);

  const handleGroupChange = useCallback((group: 'monthly' | 'annual') => {
    setSelectedGroup(group);
  }, []);

  // Fetch plan data - ONLY when Stripe tab is active or on web (where Stripe is default)
  // This prevents unnecessary API calls when IAP tab is active
  const shouldFetchStripePlans = !isNative || activeTab === 'stripe';
  

  const { data: planData, isLoading: planIsLoading, error: planError } = useQuery({
    queryKey: ['planData', 'stripe'],
    queryFn: async () => {
      const res = await apis.getProductsInternal();

      return res;
    },
    enabled: shouldFetchStripePlans, // Only fetch when Stripe tab is active
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get current plan from query cache - include companyNumber in key to ensure proper cache invalidation
  const queryClientForPlan = useQueryClient();
  // Include companyNumber in query key so subscription plan refetches when company changes
  const subscriptionPlanQueryKey = companyNumber
    ? ['subscriptionPlan', companyNumber]
    : ['subscriptionPlan'];
  const currentPlan: any = queryClientForPlan.getQueryData(subscriptionPlanQueryKey);

  // CRITICAL: Refetch billing queries when companyNumber changes (company switch)
  // This ensures billing data refreshes when user switches companies while on UpgradeScreen
  useEffect(() => {
    // Use a small debounce to batch refetch calls and prevent multiple loading states
    const timeoutId = setTimeout(() => {
      // Batch all refetch calls together
      queryClientForPlan.refetchQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            (Array.isArray(key) && (
              key[0] === 'subscriptionPlan' ||
              key[0] === 'billingInfo' ||
              (key[0] === 'planData' && shouldFetchStripePlans)
            ))
          );
        }
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [companyNumber, queryClientForPlan, shouldFetchStripePlans]);

  // Force query to refetch when switching to Stripe tab if no data available
  useEffect(() => {
    if (isNative && activeTab === 'stripe') {
      // Only refetch if we don't have data and we're not currently loading
      // This prevents multiple refetch calls
      if (!planIsLoading && !planData) {
        // Use a small delay to prevent rapid refetches
        const timeoutId = setTimeout(() => {
          queryClientForPlan.refetchQueries({ queryKey: ['planData', 'stripe'] });
        }, 200);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [activeTab, isNative, planIsLoading, planData, queryClientForPlan]);

  // Process and categorize plans - ALWAYS process when we have data, regardless of active tab
  // This ensures planGroups are ready when switching to Stripe tab
  useEffect(() => {
    // Always process if we have data, even if IAP tab is active
    // This ensures planGroups are ready when user switches to Stripe tab
    if (planIsLoading) {
      // Don't clear existing planGroups while loading
      return;
    }

    if (!planData) {
      // Only clear planGroups if we're on Stripe tab and have no data
      if (isNative && activeTab === 'stripe') {
        setPlanGroups(prev => {
          // Only update if we don't already have empty groups
          if (prev.length === 0 || prev.some(g => g.plans.length > 0)) {
            return [
              { title: 'Monthly Plans', plans: [] },
              { title: 'Annual Plans', plans: [] }
            ];
          }
          return prev;
        });
      }
      return;
    }

    // Helper function to process plan data
    const processPlans = (products: any[]) => {
      const filtered = products.filter((item) =>
        item.active &&
        item.default_price &&
        item.default_price.type === 'recurring' &&
        item.name !== 'EXEMPT_PRODUCT'
      );

      const monthlyPlans = filtered.filter((item) =>
        item.default_price.recurring?.interval === 'month'
      ).sort((a, b) => (a.default_price.unit_amount || 0) - (b.default_price.unit_amount || 0));

      const annualPlans = filtered.filter((item) =>
        item.default_price.recurring?.interval === 'year'
      ).sort((a, b) => (a.default_price.unit_amount || 0) - (b.default_price.unit_amount || 0));

      return [
        { title: 'Monthly Plans', plans: monthlyPlans },
        { title: 'Annual Plans', plans: annualPlans }
      ];
    };

    let newGroups = null;

    if (Array.isArray(planData) && planData.length > 0) {
      newGroups = processPlans(planData);
    }
    else if (planData?.success && Array.isArray(planData?.data) && planData.data.length > 0) {
      newGroups = processPlans(planData.data);
    }
    else if (planData?.success && planData?.data?.data && Array.isArray(planData.data.data)) {
      // Handle nested data structure: { success: true, data: { data: [...] } }
      newGroups = processPlans(planData.data.data);
    }

    // Only update if we have new groups or need to clear
    if (newGroups) {
      setPlanGroups(prev => {
        // Only update if the data actually changed
        const prevMonthlyCount = prev.find(g => g.title.includes('Monthly'))?.plans.length || 0;
        const prevAnnualCount = prev.find(g => g.title.includes('Annual'))?.plans.length || 0;
        const newMonthlyCount = newGroups[0]?.plans.length || 0;
        const newAnnualCount = newGroups[1]?.plans.length || 0;

        if (prevMonthlyCount !== newMonthlyCount || prevAnnualCount !== newAnnualCount) {
          return newGroups;
        }
        return prev;
      });
    } else if (isNative && activeTab === 'stripe') {
      // Only clear if we're on Stripe tab and have no valid data
      setPlanGroups(prev => {
        if (prev.length === 0 || prev.some(g => g.plans.length > 0)) {
          return [
            { title: 'Monthly Plans', plans: [] },
            { title: 'Annual Plans', plans: [] }
          ];
        }
        return prev;
      });
    }
  }, [planData, planError, isNative, activeTab, planIsLoading]);


  return (
    <DefaultScreen
      backButton
      style={{ paddingTop: Platform.select({ web: 20 }), paddingBottom: 50 }} screenTitle={'Billing'}>
      <YStack
        gap={20} flex={1}>
        {/* Combined Current Plan & Billing Management Card */}
        <Animatable.View
          animation="fadeInDown"
          duration={800}
          style={[{
            width: Platform.OS === 'web' ? "100%" : undefined,
            backgroundColor: theme ? "#ffffff" : "#1e293b",
            borderRadius: 20,
            borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
          }]}
        >
          {/* Card Header */}
          <View style={{
            padding: 24,
            borderBottomWidth: 1,
            borderBottomColor: theme ? "#e2e8f0" : "#334155"
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={{
                backgroundColor: theme ? "#ddd6fe" : "#312e81",
                borderRadius: 12,
                padding: 12,
                marginRight: 16
              }}>
                <MaterialIcons
                  name="workspace-premium"
                  size={24}
                  color={theme ? "#7c3aed" : "#a78bfa"}
                />
              </View>
              <View style={{ flex: 1 }}>
                <H6 style={{
                  color: theme ? "#1e293b" : "#f1f5f9",
                  fontWeight: '700',
                  marginBottom: 2
                }}>
                  Your Current Plan & Billing
                </H6>
                <Text style={{
                  color: theme ? "#64748b" : "#94a3b8",
                  fontSize: 14
                }}>
                  Manage your subscription and billing details
                </Text>
              </View>
            </View>
          </View>

          {/* Card Content */}
          <View style={{ padding: 24, paddingHorizontal: 10 }}>
            <XStack gap={30} flexWrap="wrap">
              {/* Current Plan Section */}
              <View style={{ minWidth: Platform.OS === 'web' ? 300 : undefined }}>
                <PlanComponent
                  minimal={false}
                  upgradeRef={upgradeRef}
                  manageRef={billingManageRef}
                  planCallback={_planCallback}
                  billingInfo={{ billingId: userBilling?.billingId, subscriptionId: userBilling?.subscriptionId || null }}
                />
              </View>

              {/* Billing Management Section */}
              <YStack gap={20} flexBasis={Platform.select({ web: 400, native: "80%" })} flexGrow={1}>
                <View style={{ minWidth: Platform.OS === 'web' ? 300 : undefined }}>
                <View style={{
                  backgroundColor: theme ? "#f8fafc" : "#0f172a",
                  borderRadius: 12,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? "#e2e8f0" : "#334155"
                }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
                    <View style={{
                      backgroundColor: theme ? "#d1fae5" : "#064e3b",
                      borderRadius: 8,
                      padding: 8,
                      marginRight: 12
                    }}>
                      <MaterialIcons
                        name="account-balance-wallet"
                        size={20}
                        color={theme ? "#059669" : "#10b981"}
                      />
                    </View>
                    <H6 style={{
                      color: theme ? "#1e293b" : "#f1f5f9",
                      fontWeight: '600'
                    }}>
                      Billing Management
                    </H6>
                  </View>

                  <Text style={{ 
                    marginBottom: 16,
                    color: theme ? "#64748b" : "#94a3b8",
                    fontSize: 14,
                    lineHeight: 20
                  }}>
                    View your billing history and manage your subscription through our secure customer portal
                  </Text>

                  <XStack gap={12} flexWrap="wrap">
                    <Pressable
                      onPress={handleCustomerPortal}
                      style={({ pressed }) => ({
                        backgroundColor: pressed
                          ? (theme ? "#047857" : "#065f46")
                          : (theme ? "#059669" : "#10b981"),
                        borderRadius: 8,
                        paddingVertical: 12,
                        paddingHorizontal: 20,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        transform: [{ scale: pressed ? 0.98 : 1 }],
                        flex: 1,
                        minWidth: 140
                      })}
                    >
                      <Feather name="external-link" size={16} color="white" />
                      <Text adjustsFontSizeToFit style={{
                        color: 'white', 
                        fontWeight: '600',
                        fontSize: 14
                      }}>
                        Manage Billing
                      </Text>
                    </Pressable>

                    {/* Cancel Plan Button */}
                    {currentPlan?.product?.name !== 'enterprise' &&
                      currentPlan?.product?.name !== 'Free Plan' &&
                      currentPlan?.product?.name !== 'free plan' &&
                      currentPlan?.product?.name !== 'EXEMPT_PLAN' && (
                        <Pressable
                          onPress={() => {
                            alertContext?.showConfirm(
                              "Cancel Subscription",
                              "Are you sure you want to cancel your subscription?",
                              {
                                confirmText: "Yes, Cancel",
                                cancelText: "Keep Plan"
                              }
                            ).then(async (confirmed) => {
                              if (confirmed) {
                                try {
                                  await handleCancelPlan(currentPlan?.type as PlanType);
                                  queryClientForPlan.refetchQueries({ queryKey: subscriptionPlanQueryKey });
                                  queryClientForPlan.refetchQueries({ queryKey: ['billingInfo', companyNumber].filter(Boolean) });

                                  if (Platform.OS === 'web') {
                                    dispatch({ type: HIDE_PAYMENT_REQUIRED });
                                    window.location.reload();
                                  }
                                } catch (error) {
                                  console.error("Error cancelling plan", error);
                                }
                              }
                            });
                          }}
                          style={({ pressed }) => ({
                            backgroundColor: pressed
                              ? (theme ? "#dc2626" : "#991b1b")
                              : (theme ? "#ef4444" : "#f87171"),
                            borderRadius: 8,
                            paddingVertical: 12,
                            paddingHorizontal: 20,
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            transform: [{ scale: pressed ? 0.98 : 1 }],
                            flex: 1,
                            minWidth: 140
                          })}
                        >
                          <Feather name="x-circle" size={16} color="white" />
                        <Text adjustsFontSizeToFit style={{
                            color: 'white',
                            fontWeight: '600',
                            fontSize: 14
                          }}>
                            Cancel Plan
                          </Text>
                        </Pressable>
                      )}

                    {/* Upgrade Plan Button */}
                    <Pressable
                      onPress={() => {
                          if (Platform.OS != 'web') {
                            scrollRef.current?.scrollToEnd({ animated: true });
                          }
                        // Scroll to bottom of page
                          else if (typeof window !== 'undefined') {
                          window.scrollTo({
                            top: document.documentElement.scrollHeight,
                            behavior: 'smooth'
                          });
                        }
                      }}
                      style={({ pressed }) => ({
                        backgroundColor: pressed
                          ? (theme ? "#1d4ed8" : "#1e40af")
                          : (theme ? "#3b82f6" : "#60a5fa"),
                        borderRadius: 8,
                        paddingVertical: 12,
                        paddingHorizontal: 20,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        transform: [{ scale: pressed ? 0.98 : 1 }],
                        flex: 1,
                        minWidth: 140
                      })}
                    >
                      <Feather name="arrow-up" size={16} color="white" />
                      <Text adjustsFontSizeToFit style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: 14
                      }}>
                        Upgrade Plan
                      </Text>
                    </Pressable>
                  </XStack>
                </View>
              </View>
                <View style={{ minWidth: Platform.OS === 'web' ? 300 : '100%' }}>
                  <View style={{
                    backgroundColor: theme ? "#f8fafc" : "#0f172a",
                    borderRadius: 12,
                    paddingVertical: 20,
                    paddingHorizontal: 10,
                    borderWidth: 1,
                    borderColor: theme ? "#e2e8f0" : "#334155"
                  }}>
                    <XStack style={{ marginBottom: 16 }}>
                      <View style={{
                        backgroundColor: theme ? "#ddd6fe" : "#312e81",
                        borderRadius: 8,
                        padding: 8,
                        marginRight: 12
                      }}>
                        <MaterialIcons
                          name="auto-awesome"
                          size={20}
                          color={theme ? "#7c3aed" : "#a78bfa"}
                        />
                      </View>
                      <H6 style={{
                        color: theme ? "#1e293b" : "#f1f5f9",
                        fontWeight: '600'
                      }}>
                        AI Generation Credits
                      </H6>
                    </XStack>
                    <Text style={{
                      marginBottom: 16,
                      color: theme ? "#64748b" : "#94a3b8",
                      fontSize: 14,
                      lineHeight: 20
                    }}>
                      Purchase AI generation credits for one-time use. Credits never expire and can be used across all AI features.
                    </Text>
                    <View style={{ minHeight: 200 }}>
                      <AICreditsComponent
                        isNative={isNative}
                        activeTab={activeTab}
                      />
                    </View>
                  </View>
                </View>
              </YStack>
            </XStack>
          </View>
        </Animatable.View>
        {/* Subscription Information Card */}
        <Animatable.View
          animation="fadeInUp"
          duration={800}
          delay={200}
          style={{
            width: Platform.OS === 'web' ? "100%" : undefined,
            backgroundColor: theme ? "#ffffff" : "#1e293b",
            borderRadius: 20,
            borderWidth: 1,
            borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
          }}
        >
          <View style={{ padding: 24 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={{
                backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
                borderRadius: 12,
                padding: 12,
                marginRight: 16
              }}>
                <MaterialIcons
                  name="info-outline"
                  size={24}
                  color={theme ? "#0ea5e9" : "#38bdf8"}
                />
              </View>
              <H6 style={{
                color: theme ? "#1e293b" : "#f1f5f9",
                fontWeight: '700',
                marginBottom: 2
              }}>
                Subscription Information
              </H6>
            </View>
            <Separator marginVertical={16} borderColor={theme ? '#e2e8f0' : '#334155'} />
            <YStack gap={Platform.select({native: 16})} paddingTop={4}>
              <InformationalText text={"Subscriptions are typically for a monthly period unless otherwise stated"} />
              <YStack gap={8} marginBottom={8}>
                <InformationalText text={"Premium subscriptions apply to both web and mobile platforms with the same account."} />
              </YStack>
              {Platform.OS === 'ios' && (
                <InformationalText text={"Review Apple's EULA for subscriptions"} link={"https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"} />
              )}
              <InformationalText text={"Please find our privacy policy here"} link={"https://smartsaas.co.uk/privacy-policy/"} />
              <InformationalText text={"Review SmartSaaS's terms & conditions for subscriptions"} link={"https://smartsaas.co.uk/terms-and-conditions/"} />

              {/* Restore Purchases Button - Required for App Store compliance */}
              {isNative && iapContextValue && iapContextValue.restorePurchases && (
                <Pressable
                  onPress={iapContextValue.restorePurchases}
                  disabled={iapContextValue.isRestoringPurchases || !iapContextValue.connected}
                  style={({ pressed }) => ({
                    backgroundColor: pressed
                      ? (theme ? "#1e40af" : "#1e3a8a")
                      : (theme ? "#3b82f6" : "#60a5fa"),
                    borderRadius: 8,
                    paddingVertical: 12,
                    paddingHorizontal: 20,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    opacity: (iapContextValue.isRestoringPurchases || !iapContextValue.connected) ? 0.6 : 1,
                    transform: [{ scale: pressed ? 0.98 : 1 }],
                    marginTop: 8
                  })}
                >
                  {iapContextValue.isRestoringPurchases ? (
                    <>
                      <Spinner size="small" color="white" />
                      <Text style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: 14
                      }}>
                        Restoring...
                      </Text>
                    </>
                  ) : (
                    <>
                      <Feather name="refresh-cw" size={16} color="white" />
                      <Text style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: 14
                      }}>
                        Restore Purchases
                      </Text>
                    </>
                  )}
                </Pressable>
              )}
            </YStack>
          </View>
        </Animatable.View>
        {/* Upgrade Plans Card */}
        <Animatable.View
          animation="fadeInUp"
          duration={800}
          delay={400}
          style={{
            backgroundColor: theme ? "#ffffff" : "#1e293b",
            borderRadius: 20,
            borderWidth: 1,
            minHeight: 700,
            borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
            overflow: 'hidden',
            ...Platform.select({
              native: {
                flex: 1,
                minHeight: 400,
              },
            }),
            width: Platform.OS === 'web' ? '100%' : '100%',
            alignSelf: 'center',
          }}
        >
          {/* Card Header */}
          <View
            style={{
              padding: 24,
              borderBottomWidth: 1,
              borderBottomColor: theme ? "#e2e8f0" : "#334155",
            }}
          >
            <YStack gap={16}>
              {/* Title Section */}
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View
                  style={{
                    backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
                    borderRadius: 12,
                    padding: 12,
                    marginRight: 16,
                  }}
                >
                  <MaterialIcons
                    name="trending-up"
                    size={24}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <H6
                    style={{
                      color: theme ? "#1e293b" : "#f1f5f9",
                      fontWeight: '700',
                      marginBottom: 2,
                    }}
                  >
                    Upgrade Your Plan
                  </H6>
                  <Text
                    style={{
                      color: theme ? "#64748b" : "#94a3b8",
                      fontSize: 14,
                    }}
                  >
                    Choose the perfect plan for your needs
                  </Text>
                </View>
              </View>

              {/* Tabs Section */}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                {/* In-App Purchase / Stripe tabs */}
                {isNative && (
                  <View
                    style={[
                      Platform.OS === 'web'
                        ? {
                          ...styles.modernRow,
                          backgroundColor: theme
                            ? 'rgba(255, 255, 255, 0.9)'
                            : 'rgba(30, 41, 59, 0.9)',
                          borderRadius: 12,
                          padding: 4,
                          borderWidth: 1,
                          borderColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        }
                        : [
                          styles.modernRow,
                          {
                            backgroundColor: theme
                              ? 'rgba(255, 255, 255, 0.9)'
                              : 'rgba(30, 41, 59, 0.9)',
                            borderRadius: 12,
                            padding: 4,
                            borderWidth: 1,
                            borderColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                          },
                        ],
                    ]}
                  >
                    {(['iap', ...(Platform.OS === 'web' ? ['stripe'] : [])] as const).map((tab) => {
                      const isActive = activeTab === tab;
                      return (
                        <Pressable
                          key={tab}
                          onPress={() => setActiveTabStable(tab)}
                          style={{
                            paddingHorizontal: 16,
                            paddingVertical: 8,
                            borderRadius: 8,
                            backgroundColor: isActive ? '#3b82f6' : 'transparent',
                            minWidth: 100,
                            alignItems: 'center',
                            flexDirection: 'row',
                            gap: 8,
                          }}
                        >
                          {isActive && (
                            <MaterialIcons
                              name={tab === 'iap' ? 'trending-up' : 'credit-card'}
                              size={16}
                              color="white"
                            />
                          )}
                          <Text
                            style={{
                              ...styles.modernFieldValue,
                              color: isActive ? 'white' : theme ? '#64748b' : '#94a3b8',
                              fontWeight: '600',
                              fontSize: 13,
                              textAlign: 'center',
                            }}
                          >
                            {tab === 'iap' ? 'In-App Purchase' : 'Stripe'}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                )}

                {/* Plan Groups toggle (Web only) */}
                {!isNative && planGroups.length > 0 && (
                  <View
                    style={{
                      ...styles.modernRow,
                      backgroundColor: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.9)',
                      borderRadius: 12,
                      padding: 4,
                      borderWidth: 1,
                      borderColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {planGroups.map((group, index) => {
                      const isActive = group.title?.toLowerCase().includes(selectedGroup);
                      return (
                        <Pressable
                          key={index}
                          onPress={() =>
                            setSelectedGroup(
                              group.title?.toLowerCase().includes('monthly') ? 'monthly' : 'annual'
                            )
                          }
                          style={{
                            paddingHorizontal: 20,
                            paddingVertical: 10,
                            borderRadius: 8,
                            backgroundColor: isActive ? '#3b82f6' : 'transparent',
                            minWidth: 100,
                            alignItems: 'center',
                          }}
                        >
                          <Text
                            style={{
                              ...styles.modernFieldValue,
                              color: isActive ? 'white' : theme ? '#64748b' : '#94a3b8',
                              fontWeight: '600',
                              fontSize: 14,
                              textAlign: 'center',
                            }}
                          >
                            {group.title}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                )}
              </View>
            </YStack>
          </View>

          {/* Card Content */}
          <View style={{ flex: 1, width: '100%' }}>
            <ScrollView
              style={{ flex: 1, width: '100%' }}
              contentContainerStyle={{
                padding: 24,
                alignItems: 'center',
                ...Platform.select({
                  web: { minHeight: 1000 },
                  native: { flexGrow: 1 },
                }),
              }}
              showsVerticalScrollIndicator={false}
              showsHorizontalScrollIndicator={false}
            >
              <ErrorBoundary>
                {isNative ? (
                  <>
                    {activeTab === 'iap' && (
                      <View style={{ width: '100%' }}>
                        {UpgradeComponentNative ? (
                          <Suspense
                            fallback={
                              <View style={{ padding: 40, alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                                <Spinner size="large" color="$blue10" />
                                <Text style={{
                                  color: theme ? '#64748b' : '#94a3b8',
                                  marginTop: 16,
                                  fontSize: 14
                                }}>
                                  Loading subscription plans...
                                </Text>
                              </View>
                            }
                          >
                            <UpgradeComponentNative
                              scrollRef={scrollRef}
                              userPlan={userPlan}
                              isActive={true}
                            />
                          </Suspense>
                        ) : (
                          <View style={{ padding: 20, alignItems: 'center' }}>
                            <Text style={{ color: theme ? '#64748b' : '#94a3b8' }}>IAP component not available</Text>
                          </View>
                        )}
                      </View>
                    )}

                    {activeTab === 'stripe' && (
                      <View style={{ width: '100%' }}>
                        <StripeUpgradeComponentNative
                          ref={upgradeRef}
                          userPlan={userPlan}
                          selectedGroup={selectedGroup}
                          onGroupChange={handleGroupChange}
                          planGroups={planGroups}
                          planIsLoading={planIsLoading}
                        />
                      </View>
                    )}

                    {activeTab !== 'iap' && activeTab !== 'stripe' && (
                      <View style={{ width: '100%', minHeight: 200, alignItems: 'center' }}>
                        <Text style={{ color: theme ? '#64748b' : '#94a3b8', textAlign: 'center' }}>
                          Loading... (activeTab: {activeTab})
                        </Text>
                      </View>
                    )}
                  </>
                ) : (
                    <View style={{ width: '100%' }}>
                      <StripeUpgradeComponent
                      ref={upgradeRef}
                      userPlan={userPlan}
                      selectedGroup={selectedGroup}
                      onGroupChange={handleGroupChange}
                      planGroups={planGroups}
                      planIsLoading={planIsLoading}
                    />
                  </View>
                )}
              </ErrorBoundary>
            </ScrollView>
          </View>
        </Animatable.View>
      </YStack>
    </DefaultScreen>
  );
}

export default UpgradeScreen;